import React, { useState, useRef } from 'react'
import { Modal, message, Divider, Button, Spin, Progress, Select } from 'antd'
import { LoadingOutlined } from '@ant-design/icons';
import { RiFileVideoLine, RiFileSettingsLine } from "react-icons/ri";
import { FiAlertCircle } from "react-icons/fi";
import { MdOutlineDone } from "react-icons/md";
import { TextField } from "@mui/material";
import { prepareData } from './dataPreparation';
import AuthContext from '../contexts/AuthContext';
import { storage } from '../firebase';
//import { uploadData } from 'aws-amplify/storage';
import axios from 'axios';
import { getDistance } from 'geolib';

// Utility function to calculate geodesic distance between two GPS coordinates using geolib
// This is more accurate than Haversine formula, similar to geopy's geodesic
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    try {
        // getDistance returns distance in meters, convert to kilometers
        const distanceInMeters = getDistance(
            { latitude: lat1, longitude: lon1 },
            { latitude: lat2, longitude: lon2 },
            1 // accuracy (1 meter)
        );
        return distanceInMeters / 1000; // Convert to kilometers
    } catch (error) {
        console.error('Error calculating distance:', error);
        return Infinity; // Return infinity if calculation fails
    }
};

// Function to calculate sub_road_id by arranging videos in sequence based on GPS proximity
// This function works with both new and existing videos to maintain proper sequencing
const calculateSubRoadIds = (videos) => {
    if (!videos || videos.length === 0) return [];
    if (videos.length === 1) {
        return [{ ...videos[0], sub_road_id: 1 }];
    }

    // Create a copy of videos array with parsed coordinates
    const videosWithIndex = videos.map((video, index) => ({
        ...video,
        originalIndex: index,
        startLat: parseFloat(video.startLat || video.start_latitude),
        startLon: parseFloat(video.startLon || video.start_longitude),
        endLat: parseFloat(video.endLat || video.end_latitude),
        endLon: parseFloat(video.endLon || video.end_longitude)
    }));

    // Sort videos to create a sequential path using geodesic distance
    const sortedVideos = [];
    const remaining = [...videosWithIndex];

    // Start with the first video
    sortedVideos.push(remaining.shift());

    // Sequentially add videos that are closest to the end of the last video in the sequence
    while (remaining.length > 0) {
        const lastVideo = sortedVideos[sortedVideos.length - 1];
        let minDistance = Infinity;
        let closestIndex = 0;

        // Find the closest video to the end point of the last video
        remaining.forEach((video, index) => {
            // Calculate geodesic distance from last video's end to this video's start
            const distanceToStart = calculateDistance(
                lastVideo.endLat, lastVideo.endLon,
                video.startLat, video.startLon
            );

            // Also consider distance from last video's end to this video's end (reverse direction)
            const distanceToEnd = calculateDistance(
                lastVideo.endLat, lastVideo.endLon,
                video.endLat, video.endLon
            );

            // Use the minimum distance
            const distance = Math.min(distanceToStart, distanceToEnd);

            if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
            }
        });

        sortedVideos.push(remaining.splice(closestIndex, 1)[0]);
    }

    // Assign sub_road_id based on the sorted order
    return sortedVideos.map((video, index) => ({
        ...video,
        sub_road_id: index + 1
    }));
};

// Function to fetch existing videos for a given road_name from the backend
const fetchExistingVideosForRoad = async (roadName, token) => {
    try {
        if (!roadName || !roadName.trim()) {
            return [];
        }

        const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}dataUpload/get_videos_by_road_name`,
            {
                params: { road_name: roadName.trim() },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        console.log('Existing videos for road:', roadName, response.data);
        return response.data.videos || response.data.result || [];
    } catch (error) {
        console.error('Error fetching existing videos:', error);
        // If endpoint doesn't exist or fails, return empty array
        return [];
    }
};

// Function to update sub_road_ids for existing videos in the database
const updateSubRoadIdsInDatabase = async (videosToUpdate, token) => {
    try {
        if (!videosToUpdate || videosToUpdate.length === 0) {
            return;
        }

        // Prepare update payload with uid and new sub_road_id
        const updates = videosToUpdate.map(video => ({
            uid: video.uid || video.video_uid,
            sub_road_id: video.sub_road_id
        }));

        console.log('Updating sub_road_ids in database:', updates);

        await axios.post(
            `${process.env.REACT_APP_BASE_URL}dataUpload/update_sub_road_ids`,
            { updates },
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        console.log('Successfully updated sub_road_ids in database');
    } catch (error) {
        console.error('Error updating sub_road_ids:', error);
        throw error;
    }
};


const Upload = ({ visible, onClose, defaultRoadId, defaultSurvey, defaultTimeOfDay, defaultSide, defaultCarriageway }) => {
    const { user } = React.useContext(AuthContext);
    // Multiple selection state
    const [videoFiles, setVideoFiles] = useState([]); // File[]
    const [gpxFiles, setGpxFiles] = useState([]); // File[] corresponding by index to videoFiles
    const [perFileCoords, setPerFileCoords] = useState([]); // [{startLat, startLon, endLat, endLon, gpxName}]
    const [roadId, setRoadId] = useState("");
    const [roadName, setRoadName] = useState("");
    const [startLatitude, setStartLatitude] = useState("");
    const [startLongitude, setStartLongitude] = useState("");
    const [endLatitude, setEndLatitude] = useState("");
    const [endLongitude, setEndLongitude] = useState("");
    const [roadType, setRoadType] = useState("LHS");
    // --- NHAI folder hierarchy selectors ---
    // Each option has a human-readable label shown in the UI and a compact,
    // space-free value used as the folder name in the bucket.
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const TIME_OF_DAY_OPTIONS = [
        { label: 'Day Time Survey', value: 'day' },
        { label: 'Night Time Survey', value: 'night' },
    ];
    const SIDE_OPTIONS = [
        { label: 'LHS', value: 'LHS' },
        { label: 'RHS', value: 'RHS' },
    ];
    const CARRIAGEWAY_OPTIONS = [
        { label: 'MCW (Main Carriageway)', value: 'MCW' },
        { label: 'Service Road', value: 'service-road' },
        { label: 'Slip Road', value: 'slip-road' },
    ];
    const currentYear = new Date().getFullYear();
    const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    const WEEK_OPTIONS = Array.from({ length: 53 }, (_, i) => i + 1);

    const [surveyWeek, setSurveyWeek] = useState("");
    const [surveyMonth, setSurveyMonth] = useState("");
    const [surveyYear, setSurveyYear] = useState("");
    const [timeOfDay, setTimeOfDay] = useState("");
    const [side, setSide] = useState("");
    const [carriageway, setCarriageway] = useState("");
    const [existingFolders, setExistingFolders] = useState({
        survey: [],
        timeOfDay: [],
        side: [],
        carriageway: [],
    });

    // Folder value: dash-separated, no spaces (e.g. "Week-18-May-2025").
    const surveyLabel = (surveyWeek && surveyMonth && surveyYear)
        ? `Week-${surveyWeek}-${surveyMonth}-${surveyYear}`
        : '';

    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [preparedVideoFile, setPreparedVideoFile] = useState(null); // kept for backward compat (single display)
    const [preparedGpxFile, setPreparedGpxFile] = useState(null);
    const [preparedItems, setPreparedItems] = useState([]); // [{uid, data, video: File, gpx: File, index}]
    const [isPrepared, setIsPrepared] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [perFileProgress, setPerFileProgress] = useState([]); // number[] 0-100 per file
    const [abortController, setAbortController] = useState(new AbortController());
    const [uid, setUid] = useState("");
    const [data, setData] = useState(null);
    const videoInputRef = useRef(null);
    const gpxInputRef = useRef(null);
    const [isConverting, setIsConverting] = useState(false);
    const [roadDatabase, setRoadDatabase] = useState([]);
    const [loadingRoads, setLoadingRoads] = useState(false);
    const [existingVideosToUpdate, setExistingVideosToUpdate] = useState([]); // Track existing videos that need sub_road_id update

    // Fetch road database when modal opens
    React.useEffect(() => {
        if (visible) {
            fetchRoadDatabase();
        }
    }, [visible]);

    // Pre-select road when defaultRoadId is provided and roads are loaded
    React.useEffect(() => {
        if (defaultRoadId && roadDatabase.length > 0) {
            const match = roadDatabase.find(r => r.road_id === defaultRoadId);
            if (match) {
                setRoadId(match.road_id);
                setRoadName(match.road_name || '');
                setRoadType(match.road_type || 'LHS');
            }
        }
    }, [defaultRoadId, roadDatabase]);

    // Prefill survey/time/side/carriageway when the modal is opened with defaults
    // (used by "Upload here" in the Paths modal). Only runs when the modal opens.
    React.useEffect(() => {
        if (!visible) return;
        if (defaultSurvey) {
            // Survey format: "Week-{n}-{Mon}-{YYYY}", e.g. "Week-18-May-2025"
            const m = /^Week-(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(defaultSurvey);
            if (m) {
                setSurveyWeek(m[1]);
                setSurveyMonth(m[2]);
                setSurveyYear(m[3]);
            }
        }
        if (defaultTimeOfDay) setTimeOfDay(defaultTimeOfDay);
        if (defaultSide) setSide(defaultSide);
        if (defaultCarriageway) setCarriageway(defaultCarriageway);
    }, [visible, defaultSurvey, defaultTimeOfDay, defaultSide, defaultCarriageway]);

    // Fetch existing subfolders at each level so dropdowns can flag "(exists)" entries.
    // Cascades: roadId -> survey list; +survey -> time list; +time -> side list; +side -> carriageway list.
    const fetchNhaiFolders = async (params) => {
        try {
            const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}dataUpload/list-nhai-folders`,
                {
                    params,
                    headers: { 'Authorization': `Bearer ${token}` },
                }
            );
            return response.data.folders || [];
        } catch (error) {
            console.error('Error listing NHAI folders:', error);
            return [];
        }
    };

    React.useEffect(() => {
        if (!roadId) {
            setExistingFolders({ survey: [], timeOfDay: [], side: [], carriageway: [] });
            return;
        }
        fetchNhaiFolders({ roadId }).then(folders => {
            setExistingFolders(prev => ({ ...prev, survey: folders, timeOfDay: [], side: [], carriageway: [] }));
        });
    }, [roadId]);

    React.useEffect(() => {
        if (!roadId || !surveyLabel) {
            setExistingFolders(prev => ({ ...prev, timeOfDay: [], side: [], carriageway: [] }));
            return;
        }
        fetchNhaiFolders({ roadId, survey: surveyLabel }).then(folders => {
            setExistingFolders(prev => ({ ...prev, timeOfDay: folders, side: [], carriageway: [] }));
        });
    }, [roadId, surveyLabel]);

    React.useEffect(() => {
        if (!roadId || !surveyLabel || !timeOfDay) {
            setExistingFolders(prev => ({ ...prev, side: [], carriageway: [] }));
            return;
        }
        fetchNhaiFolders({ roadId, survey: surveyLabel, timeOfDay }).then(folders => {
            setExistingFolders(prev => ({ ...prev, side: folders, carriageway: [] }));
        });
    }, [roadId, surveyLabel, timeOfDay]);

    React.useEffect(() => {
        if (!roadId || !surveyLabel || !timeOfDay || !side) {
            setExistingFolders(prev => ({ ...prev, carriageway: [] }));
            return;
        }
        fetchNhaiFolders({ roadId, survey: surveyLabel, timeOfDay, side }).then(folders => {
            setExistingFolders(prev => ({ ...prev, carriageway: folders }));
        });
    }, [roadId, surveyLabel, timeOfDay, side]);

    const previewPrefix = (roadId && surveyLabel && timeOfDay && side && carriageway)
        ? `nhai-upload/${roadId}/${surveyLabel}/${timeOfDay}/${side}/${carriageway}/`
        : '';

    // GCS console deep-link to the folder. Each segment is URL-encoded so values
    // with spaces or special chars (future-proofing) don't break the link.
    const gcsConsoleUrl = (roadId && surveyLabel && timeOfDay && side && carriageway)
        ? `https://console.cloud.google.com/storage/browser/nhai-upload/${[roadId, surveyLabel, timeOfDay, side, carriageway].map(encodeURIComponent).join('/')}/?project=roadvisiondata`
        : '';

    const [creatingFolder, setCreatingFolder] = useState(false);
    const allFolderFieldsSelected = !!(roadId && surveyLabel && timeOfDay && side && carriageway);

    const handleCreateFolder = async () => {
        if (!allFolderFieldsSelected) {
            setErrorMessage('Pick Road, Survey (Week/Month/Year), Time, Side and Carriageway first');
            return;
        }
        setErrorMessage('');
        setCreatingFolder(true);
        try {
            const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}dataUpload/create-nhai-folder`,
                { roadId, survey: surveyLabel, timeOfDay, side, carriageway },
                { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
            );
            const { presignedUrl, prefix } = response.data || {};
            if (!presignedUrl) {
                throw new Error('Server did not return a signed URL');
            }
            // PUT a zero-byte object at "{prefix}/" — same convention the GCS console uses.
            const putResp = await fetch(presignedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/x-directory' },
                body: new Blob([], { type: 'application/x-directory' }),
            });
            if (!putResp.ok) {
                throw new Error(`Bucket rejected placeholder PUT (${putResp.status})`);
            }
            message.success(`Folder created: ${prefix}/`);
            // Refresh existence flags so the new folder is marked "(exists)".
            const folders = await fetchNhaiFolders({ roadId, survey: surveyLabel, timeOfDay, side });
            setExistingFolders(prev => ({ ...prev, carriageway: folders }));
            // Offer to jump straight to the new folder in the GCS console.
            Modal.confirm({
                title: 'Folder created',
                content: `Open ${prefix}/ in the GCS Console?`,
                okText: 'Open in GCS Console',
                cancelText: 'Stay here',
                onOk: () => {
                    if (gcsConsoleUrl) window.open(gcsConsoleUrl, '_blank', 'noopener,noreferrer');
                },
            });
        } catch (error) {
            console.error('Error creating folder:', error);
            setErrorMessage(`Failed to create folder: ${error.response?.data?.error || error.message}`);
        } finally {
            setCreatingFolder(false);
        }
    };

    const fetchRoadDatabase = async () => {
        try {
            setLoadingRoads(true);
            const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}dataUpload/get_excel_data`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const roads = response.data.result || [];

            console.log('Fetched road database from API:', roads);
            console.log('Sample road data:', roads[0]);

            // Filter only roads that have road_id
            const validRoads = roads.filter(road => road.road_id);
            setRoadDatabase(validRoads);

            console.log(`Loaded ${validRoads.length} roads with Road IDs`);
        } catch (error) {
            console.error('Error fetching road database:', error);
            message.error('Failed to load road database');
        } finally {
            setLoadingRoads(false);
        }
    };

    const handleVideoFileSelect = () => {
        if (videoInputRef.current) {
            videoInputRef.current.click();
        }
    };

    const handleGpxFileSelect = () => {
        if (gpxInputRef.current) {
            gpxInputRef.current.click();
            if (errorMessage.includes("GPX file")) {
                setErrorMessage("");
            }
        }
    };

    const handleVideoFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        setVideoFiles((prev) => {
            const combined = [...prev, ...files];
            if (combined.length > 50) {
                message.warning("Maximum 50 videos allowed. Keeping first 50.");
                return combined.slice(0, 50);
            }
            message.success(`Added ${files.length} video(s). Total: ${combined.length}`);
            return combined;
        });
    };

    const parseSingleGpx = async (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    let gpxContent = event.target.result;
                    if (typeof gpxContent === 'string' && gpxContent.startsWith('<? xml')) {
                        gpxContent = gpxContent.replace('<? xml', '<?xml').replace(/\s+=\s+/g, '=').replace(/\s+\?>/, '?>');
                    }
                    const parser = new DOMParser();
                    let xmlDoc = parser.parseFromString(gpxContent, 'text/xml');
                    const errorNode = xmlDoc.getElementsByTagName('parsererror');
                    if (errorNode.length > 0) {
                        throw new Error('Invalid GPX XML format');
                    }
                    let gpxElement = xmlDoc.getElementsByTagName('gpx')[0];
                    let trkptElements = xmlDoc.getElementsByTagNameNS('*', 'trkpt');
                    if (!gpxElement || trkptElements.length === 0) {
                        const sampleGpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1"><trk><trkseg><trkpt lat="26.276730" lon="73.001525"><time>${new Date().toISOString()}</time><ele>260</ele></trkpt></trkseg></trk></gpx>`;
                        xmlDoc = parser.parseFromString(sampleGpx, 'text/xml');
                        gpxElement = xmlDoc.getElementsByTagName('gpx')[0];
                        trkptElements = xmlDoc.getElementsByTagNameNS('*', 'trkpt');
                    } else {
                        gpxElement.setAttribute('version', '1.1');
                        gpxElement.setAttribute('xmlns', 'http://www.topografix.com/GPX/1/1');
                        gpxElement.setAttribute('xmlns:gpxtpx', 'http://www.garmin.com/xmlschemas/TrackPointExtension/v1');
                        Array.from(trkptElements).forEach((trkpt) => {
                            if (trkpt && trkpt.nodeType === 1) {
                                if (!trkpt.hasAttribute('lat')) trkpt.setAttribute('lat', '26.276730');
                                if (!trkpt.hasAttribute('lon')) trkpt.setAttribute('lon', '73.001525');
                                const timeTags = trkpt.getElementsByTagName('time');
                                if (timeTags.length === 0) {
                                    const timeEl = xmlDoc.createElement('time');
                                    timeEl.textContent = new Date().toISOString();
                                    trkpt.appendChild(timeEl);
                                }
                                const eleTags = trkpt.getElementsByTagName('ele');
                                if (eleTags.length === 0) {
                                    const eleEl = xmlDoc.createElement('ele');
                                    eleEl.textContent = '0';
                                    trkpt.appendChild(eleEl);
                                }
                            }
                        });
                    }
                    let coords = null;
                    if (trkptElements.length > 0) {
                        const firstPoint = trkptElements[0];
                        const lastPoint = trkptElements[trkptElements.length - 1];
                        coords = {
                            startLat: firstPoint.getAttribute('lat') || '',
                            startLon: firstPoint.getAttribute('lon') || '',
                            endLat: lastPoint.getAttribute('lat') || '',
                            endLon: lastPoint.getAttribute('lon') || ''
                        };
                    }
                    const serializer = new XMLSerializer();
                    const correctedGpxContent = serializer.serializeToString(xmlDoc);
                    const correctedBlob = new Blob([correctedGpxContent], { type: 'application/gpx+xml' });
                    const correctedFile = new File([correctedBlob], file.name, { type: 'application/gpx+xml' });
                    resolve({ correctedFile, coords });
                } catch (err) {
                    const fallbackGpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1"><trk><trkseg><trkpt lat="26.276730" lon="73.001525"><time>${new Date().toISOString()}</time><ele>260</ele></trkpt></trkseg></trk></gpx>`;
                    const fallbackBlob = new Blob([fallbackGpx], { type: 'application/gpx+xml' });
                    const fallbackFile = new File([fallbackBlob], file.name, { type: 'application/gpx+xml' });
                    resolve({ correctedFile: fallbackFile, coords: { startLat: '26.276730', startLon: '73.001525', endLat: '26.276730', endLon: '73.001525' } });
                }
            };
            reader.onerror = () => {
                const fallbackGpx = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1"><trk><trkseg><trkpt lat="26.276730" lon="73.001525"><time>${new Date().toISOString()}</time><ele>260</ele></trkpt></trkseg></trk></gpx>`;
                const fallbackBlob = new Blob([fallbackGpx], { type: 'application/gpx+xml' });
                const fallbackFile = new File([fallbackBlob], file.name, { type: 'application/gpx+xml' });
                resolve({ correctedFile: fallbackFile, coords: { startLat: '26.276730', startLon: '73.001525', endLat: '26.276730', endLon: '73.001525' } });
            };
            reader.readAsText(file);
        });
    };

    const handleGpxFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 50) {
            message.error("You can upload up to 50 GPX files at a time");
        }
        const limited = files.slice(0, 50);
        if (errorMessage.includes("GPX file")) {
            setErrorMessage("");
        }
        // --- NEW: Store raw GPX files without parsing, accumulate selections ---
        setGpxFiles((prev) => {
            const combined = [...prev, ...limited];
            if (combined.length > 50) {
                message.warning("Maximum 50 GPX files allowed. Keeping first 50.");
                return combined.slice(0, 50);
            }
            message.success(`Added ${limited.length} GPX file(s). Total: ${combined.length}`);
            return combined;
        });

        /* --- OLD: GPX Parsing (commented out) ---
        setIsConverting(true);
        const results = await Promise.all(limited.map(parseSingleGpx));
        const corrected = results.map(r => r.correctedFile);
        const coords = results.map((r, idx) => ({ ...r.coords, gpxName: limited[idx]?.name }));
        setGpxFiles(corrected);
        setPerFileCoords(coords);
        if (coords[0]) {
            setStartLatitude(coords[0].startLat || '');
            setStartLongitude(coords[0].startLon || '');
            setEndLatitude(coords[0].endLat || '');
            setEndLongitude(coords[0].endLon || '');
        }
        message.success(`Processed ${corrected.length} GPX file(s)`);
        setIsConverting(false);
        --- END OLD --- */
    };
    
    const handleRoadNameSelect = (value) => {
        setRoadName(value);

        // Auto-fill Road ID and Road Type from the selected road name
        const selectedRoad = roadDatabase.find(road => road.road_name === value);
        if (selectedRoad) {
            setRoadId(selectedRoad.road_id || '');
            setRoadType(selectedRoad.road_type || 'LHS');

            console.log('Auto-filled road details from Road Name:', {
                road_name: value,
                road_id: selectedRoad.road_id,
                road_type: selectedRoad.road_type
            });

            message.info('Road ID and type auto-filled from database. Upload GPX file to fill coordinates.');
        }
    };

    const handleCancel = () => {
        setVideoFiles([]);
        setGpxFiles([]);
        setPerFileCoords([]);
        setRoadId("");
        setRoadName("");
        setStartLatitude("");
        setStartLongitude("");
        setEndLatitude("");
        setEndLongitude("");
        setRoadType("LHS");
        setSurveyWeek("");
        setSurveyMonth("");
        setSurveyYear("");
        setTimeOfDay("");
        setSide("");
        setCarriageway("");
        setExistingFolders({ survey: [], timeOfDay: [], side: [], carriageway: [] });
        setPreparedVideoFile(null);
        setPreparedGpxFile(null);
        setPreparedItems([]);
        setExistingVideosToUpdate([]);
        setErrorMessage("");
        setIsPrepared(false);
        onClose();
    };


    const uploadVideoFile = (presignedUrl, file, onProgress) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', presignedUrl);
            xhr.setRequestHeader('Content-Type', 'video/mp4');
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error(`Video upload failed: ${xhr.status} ${xhr.statusText}`));
            };
            xhr.onerror = () => reject(new Error('Network error during video upload'));
            xhr.send(file);
        });
        /* --- OLD: Axios PUT (commented out — CORS preflight issues + silent error swallowing) ---
        try {
            await axios.put(presignedUrl, file, {
                headers: { 'Content-Type': 'video/mp4' },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    onProgress(progress);
                },
                signal: abortController.signal,
            });
        } catch (error) {
            if (axios.isCancel(error)) { console.log('Upload paused'); }
            else { console.error('Error during video upload:', error); }
        }
        --- END OLD --- */
    };

    const handleSubmit = async () => {
        // Upload to NHAI bucket under: datanh11/nhai-upload/{roadId}/{survey}/{timeOfDay}/{side}/{carriageway}/
        // GCS auto-creates the prefix; no separate "create folder" call needed.
        const missingFields = [];
        if (!videoFiles || videoFiles.length === 0) missingFields.push("Video files");
        if (!roadId) missingFields.push("Road ID (select a Road Name first)");
        if (!surveyLabel) missingFields.push("Survey (Week / Month / Year)");
        if (!timeOfDay) missingFields.push("Survey Time (Day / Night)");
        if (!side) missingFields.push("Side (LHS / RHS)");
        if (!carriageway) missingFields.push("Carriageway type");

        if (missingFields.length > 0) {
            setErrorMessage(`${missingFields.join(", ")} is required`);
            return;
        }
        // GPX is optional, but if any are provided the count must match the videos.
        if (gpxFiles.length > 0 && videoFiles.length !== gpxFiles.length) {
            setErrorMessage("If GPX files are provided, their count must match videos. Otherwise remove all GPX files.");
            return;
        }
        if (videoFiles.length > 50 || gpxFiles.length > 50) {
            setErrorMessage("Maximum 50 videos and 50 GPX files at a time");
            return;
        }

        setIsUploading(true);
        setLoading(true);
        setPerFileProgress(Array(videoFiles.length).fill(0));

        try {
            const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
            if (!token) {
                setErrorMessage('Authorization token is missing. Please login again.');
                return;
            }

            // --- Parallel upload: all video (+ optional GPX) pairs upload simultaneously ---
            await Promise.all(videoFiles.map(async (videoFile, index) => {
                const gpxFile = gpxFiles[index]; // may be undefined when GPX wasn't provided

                console.log(`📤 Uploading ${index + 1}/${videoFiles.length}: ${videoFile.name}`);

                const response = await axios.post(
                    `${process.env.REACT_APP_BASE_URL}dataUpload/generate-presigned-urls-nhai`,
                    {
                        roadId,
                        survey: surveyLabel,
                        timeOfDay,
                        side,
                        carriageway,
                        videoFileName: videoFile.name,
                        gpxFileName: gpxFile ? gpxFile.name : null,
                    },
                    { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
                );

                const { videoPresignedUrl, gpxPresignedUrl } = response.data;
                if (!videoPresignedUrl) {
                    throw new Error('Failed to generate presigned URL for video');
                }
                if (gpxFile && !gpxPresignedUrl) {
                    throw new Error('Failed to generate presigned URL for GPX');
                }

                // Upload raw GPX (no parsing) only when one was provided for this index
                if (gpxFile && gpxPresignedUrl) {
                    const gpxUploadResponse = await fetch(gpxPresignedUrl, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/gpx+xml' },
                        body: gpxFile,
                    });
                    if (!gpxUploadResponse.ok) {
                        throw new Error(`Failed to upload GPX file: ${gpxFile.name}`);
                    }
                }

                // Upload video — overall progress = average across all files
                await uploadVideoFile(videoPresignedUrl, videoFile, (p) => {
                    setPerFileProgress((prev) => {
                        const next = [...prev];
                        next[index] = p;
                        const overallProgress = Math.round(next.reduce((sum, v) => sum + v, 0) / videoFiles.length);
                        setUploadProgress(overallProgress);
                        return next;
                    });
                });

                console.log(`✅ Video ${index + 1}/${videoFiles.length} uploaded: ${videoFile.name}`);
            }));

            message.success(`Uploaded ${videoFiles.length} file(s) successfully`);
            handleCancel();
        } catch (error) {
            console.error("Error during upload:", error);
            setErrorMessage(`Error during upload: ${error.message}`);
        } finally {
            setLoading(false);
            setIsUploading(false);
        }

        /* --- OLD: Two-phase upload (prepare + upload) commented out ---
        if (isPrepared) {
            // Upload phase
            setIsUploading(true);
            setLoading(true);
            try {
                const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
                if (!token) { setErrorMessage('Authorization token is missing. Please login again.'); return; }
                setPerFileProgress(Array(preparedItems.length).fill(0));
                for (let index = 0; index < preparedItems.length; index++) {
                    const item = preparedItems[index];
                    const response = await axios.post(`${process.env.REACT_APP_BASE_URL}dataUpload/generate-presigned-urls`,
                        { videoFileName: `public/${item.uid}/file.mp4`, dataFileName: `public/${item.uid}/data.txt` },
                        { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
                    );
                    const { videoPresignedUrl, dataPresignedUrl } = response.data;
                    if (!videoPresignedUrl || !dataPresignedUrl) throw new Error('Failed to generate presigned URLs');
                    const dataTxtContent = JSON.stringify(item.data, null, 2);
                    const dataUploadResponse = await fetch(dataPresignedUrl, { method: 'PUT', headers: { 'Content-Type': 'text/plain' }, body: dataTxtContent });
                    if (!dataUploadResponse.ok) throw new Error('Failed to upload data.txt file');
                    await uploadVideoFile(videoPresignedUrl, item.video, (p) => {
                        setPerFileProgress((prev) => {
                            const next = [...prev]; next[index] = p;
                            setUploadProgress(Math.round(((index + p / 100) / preparedItems.length) * 100));
                            return next;
                        });
                    });
                    const videoMetadata = {
                        uid: item.uid, road_id: roadId || null, sub_road_id: item.sub_road_id || 1,
                        project_title: roadName, starting_address: item.data.startAddress, ending_address: item.data.endAddress,
                        start_latitude: item.data.startLatitude, start_longitude: item.data.startLongitude,
                        end_latitude: item.data.endLatitude, end_longitude: item.data.endLongitude,
                        road_type: roadType || item.data.roadType, created: item.data.createdAt,
                        meta_data: { videoLength: item.data.videoLength, fileSize: item.data.fileSize }
                    };
                    await axios.post(`${process.env.REACT_APP_BASE_URL}dataUpload/video_upload`, videoMetadata,
                        { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
                    );
                }
                message.success(`Uploaded ${preparedItems.length} file(s) successfully`);
                if (existingVideosToUpdate.length > 0) {
                    await updateSubRoadIdsInDatabase(existingVideosToUpdate, token);
                }
                handleCancel();
            } catch (error) {
                setErrorMessage(`Error during upload: ${error.message}`);
            } finally { setLoading(false); setIsUploading(false); }
        } else {
            // Preparation phase
            const missingFields = [];
            if (!videoFiles || videoFiles.length === 0) missingFields.push("Video files");
            if (!gpxFiles || gpxFiles.length === 0) missingFields.push("GPX files");
            if (missingFields.length > 0) { setErrorMessage(`${missingFields.join(", ")} is required`); return; }
            if (videoFiles.length !== gpxFiles.length) { setErrorMessage("Videos count must match GPX count"); return; }
            if (videoFiles.length > 50 || gpxFiles.length > 50) { setErrorMessage("Maximum 50 videos and 50 GPX files at a time"); return; }
            setErrorMessage(""); setLoading(true);
            try {
                const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
                const defaultRoadName = roadName.trim() || `Road_${new Date().getTime()}`;
                const existingVideos = await fetchExistingVideosForRoad(defaultRoadName, token);
                const prepared = [];
                for (let i = 0; i < videoFiles.length; i++) {
                    const coords = perFileCoords[i] || {};
                    const { data: d, uid: u } = await prepareData(videoFiles[i], gpxFiles[i], defaultRoadName, user.email, user.name, undefined, undefined, undefined, undefined, roadType, coords.startLat || startLatitude, coords.startLon || startLongitude, coords.endLat || endLatitude, coords.endLon || endLongitude);
                    prepared.push({ uid: u, data: d, video: videoFiles[i], gpx: gpxFiles[i], index: i, startLat: coords.startLat || startLatitude, startLon: coords.startLon || startLongitude, endLat: coords.endLat || endLatitude, endLon: coords.endLon || endLongitude, isNew: true });
                }
                const allVideos = [...existingVideos.map(v => ({ ...v, isNew: false, uid: v.uid || v.video_uid })), ...prepared];
                const allVideosWithSubRoadIds = calculateSubRoadIds(allVideos);
                const newVideosWithSubRoadIds = allVideosWithSubRoadIds.filter(v => v.isNew);
                const existingVideosWithNewSubRoadIds = allVideosWithSubRoadIds.filter(v => !v.isNew);
                const existingVideosNeedUpdate = existingVideosWithNewSubRoadIds.filter(video => {
                    const originalVideo = existingVideos.find(v => (v.uid || v.video_uid) === video.uid);
                    return originalVideo && originalVideo.sub_road_id !== video.sub_road_id;
                });
                setExistingVideosToUpdate(existingVideosNeedUpdate.length > 0 ? existingVideosNeedUpdate : []);
                setPreparedItems(newVideosWithSubRoadIds);
                setPerFileProgress(Array(newVideosWithSubRoadIds.length).fill(0));
                if (newVideosWithSubRoadIds[0]) {
                    setUid(newVideosWithSubRoadIds[0].uid); setData(newVideosWithSubRoadIds[0].data);
                    setPreparedVideoFile(newVideosWithSubRoadIds[0].data.filePath); setPreparedGpxFile('data.txt');
                }
                setIsPrepared(true);
                message.success(`Prepared ${newVideosWithSubRoadIds.length} new video(s). Ready to upload.`);
            } catch (error) {
                setErrorMessage(`Error during preparation: ${error.message}`);
            } finally { setLoading(false); }
        }
        --- END OLD --- */
    };


    return (
        <Modal
            title={'Upload Road Imagery'}
            centered
            closable={true}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width="35vw"
            modal
        >
            <>
                <div className='flex flex-col'>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Upload Video File
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            Your selected video file must contain road imagery
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#0A2540', fontWeight: '500', marginBottom: 6, backgroundColor: '#F0F5FE', padding: '4px 8px', borderRadius: 4, width: 'fit-content' }}>
                            Supported format: .MP4
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            <div
                                onClick={!isPrepared ? handleVideoFileSelect : null}
                                style={{
                                    width: 'fit-content', borderRadius: 8, display: 'flex', alignItems: 'center',
                                    marginTop: 4, backgroundColor: 'var(--primary-color)', color: '#FFFFFF', padding: '6px 12px',
                                    cursor: !isPrepared ? 'pointer' : 'default', fontSize: '0.875rem', fontWeight: '500'
                                }}
                            >
                                <RiFileVideoLine style={{ marginRight: 6 }} />
                                {preparedVideoFile || (videoFiles.length > 0 ? 'Reselect video files' : 'Select video files from storage')}
                            </div>
                            {(preparedVideoFile || videoFiles.length > 0) && (
                                <div style={{ marginLeft: 10, marginTop: 4, fontSize: '0.85rem', color: '#5F6368' }}>
                                    {preparedVideoFile || `${videoFiles.length} video(s) selected`}
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Upload GPX File <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#5F6368' }}>(optional)</span>
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            Optional. If provided, the count must match videos and each GPX uploads alongside its paired video.
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#0A2540', fontWeight: '500', marginBottom: 6, backgroundColor: '#F0F5FE', padding: '4px 8px', borderRadius: 4, width: 'fit-content' }}>
                            Supported format: .GPX (GPS Exchange Format)
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                            <div
                                onClick={!isPrepared && !isConverting ? handleGpxFileSelect : null}
                                style={{
                                    width: 'fit-content', borderRadius: 8, display: 'flex', alignItems: 'center',
                                    marginTop: 4, backgroundColor: 'var(--primary-color)', color: '#FFFFFF', padding: '6px 12px',
                                    cursor: !isPrepared && !isConverting ? 'pointer' : 'default', fontSize: '0.875rem', fontWeight: '500'
                                }}
                            >
                                <RiFileSettingsLine style={{ marginRight: 6 }} />
                                {isConverting ? (
                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                        <Spin size="small" style={{ marginRight: 8 }} />
                                        Converting GPX file...
                                    </span>
                                ) : (
                                    preparedGpxFile || (gpxFiles.length > 0 ? 'Reselect GPX files' : 'Select GPX files from storage')
                                )}
                            </div>
                            {(preparedGpxFile || gpxFiles.length > 0) && !isConverting && (
                                <div style={{ marginLeft: 10, marginTop: 4, fontSize: '0.85rem', color: '#5F6368' }}>
                                    {preparedGpxFile || `${gpxFiles.length} GPX file(s) selected`}
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Road Name
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            This will auto-fill Road ID and Road Type
                        </p>
                        <Select
                            showSearch
                            value={roadName || undefined}
                            onChange={handleRoadNameSelect}
                            disabled={isPrepared || loadingRoads}
                            placeholder={loadingRoads ? "Loading roads..." : "Select a Road Name"}
                            style={{
                                width: '100%',
                                height: '40px',
                                backgroundColor: '#F0F5FE'
                            }}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {roadDatabase.map((road) => (
                                <Select.Option key={road.road_id} value={road.road_name}>
                                    {road.road_name}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Road ID
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            Auto-detected from selected Road Name
                        </p>
                        <TextField
                            type="text"
                            name="roadId"
                            fullWidth
                            placeholder="Road ID"
                            value={roadId}
                            disabled={true}
                            variant="outlined"
                            style={{ backgroundColor: '#E5E7EB', width: '100%', borderColor: '#0071E3', borderRadius: 8 }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 40,
                                    borderRadius: 2,
                                },
                                '& .MuiInputBase-input': {
                                    padding: '8px 2px',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                        borderRadius: 2,
                                    },
                                },
                            }}
                        />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Starting Point Coordinates
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            {gpxFiles.length > 0 ? 'Auto-filled from first GPX file; each upload uses its own GPX' : 'Will be auto-filled when you upload GPX files'}
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <TextField
                                type="text"
                                name="startLatitude"
                                placeholder="Start Latitude"
                                value={startLatitude}
                                onChange={(e) => setStartLatitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                            <TextField
                                type="text"
                                name="startLongitude"
                                placeholder="Start Longitude"
                                value={startLongitude}
                                onChange={(e) => setStartLongitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Ending Point Coordinates
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            {gpxFiles.length > 0 ? 'Auto-filled from first GPX file; each upload uses its own GPX' : 'Will be auto-filled when you upload GPX files'}
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <TextField
                                type="text"
                                name="endLatitude"
                                placeholder="End Latitude"
                                value={endLatitude}
                                onChange={(e) => setEndLatitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                            <TextField
                                type="text"
                                name="endLongitude"
                                placeholder="End Longitude"
                                value={endLongitude}
                                onChange={(e) => setEndLongitude(e.target.value)}
                                disabled={isPrepared || gpxFiles.length > 0}
                                variant="outlined"
                                style={{ backgroundColor: gpxFiles.length > 0 ? '#E5E7EB' : '#F0F5FE', width: '50%', borderColor: '#0071E3', borderRadius: 8 }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 40,
                                        borderRadius: 2,
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 2px',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                            borderRadius: 2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'black',
                                            borderRadius: 2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#80d4ff',
                                            borderRadius: 2,
                                        },
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Survey (Week-Month-Year)
                        </h1>
                        <p style={{ fontSize: '0.875rem', color: '#5F6368', marginBottom: 4 }}>
                            Builds the survey folder e.g. "Week 18-May-2025". Existing folders are marked.
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Select
                                value={surveyWeek || undefined}
                                onChange={(v) => setSurveyWeek(v)}
                                placeholder="Week"
                                style={{ width: '33%', height: '40px', backgroundColor: '#F0F5FE' }}
                            >
                                {WEEK_OPTIONS.map(w => (
                                    <Select.Option key={w} value={String(w)}>{`Week ${w}`}</Select.Option>
                                ))}
                            </Select>
                            <Select
                                value={surveyMonth || undefined}
                                onChange={(v) => setSurveyMonth(v)}
                                placeholder="Month"
                                style={{ width: '33%', height: '40px', backgroundColor: '#F0F5FE' }}
                            >
                                {MONTHS.map(m => (
                                    <Select.Option key={m} value={m}>{m}</Select.Option>
                                ))}
                            </Select>
                            <Select
                                value={surveyYear || undefined}
                                onChange={(v) => setSurveyYear(v)}
                                placeholder="Year"
                                style={{ width: '34%', height: '40px', backgroundColor: '#F0F5FE' }}
                            >
                                {YEAR_OPTIONS.map(y => (
                                    <Select.Option key={y} value={String(y)}>{y}</Select.Option>
                                ))}
                            </Select>
                        </div>
                        {surveyLabel && (
                            <div style={{ marginTop: 4, fontSize: '0.8rem', color: existingFolders.survey.includes(surveyLabel) ? '#0A7A1D' : '#5F6368' }}>
                                {surveyLabel} {existingFolders.survey.includes(surveyLabel) ? '— folder exists, files will be added' : '— new folder will be created'}
                            </div>
                        )}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Survey Time
                        </h1>
                        <Select
                            value={timeOfDay || undefined}
                            onChange={(v) => setTimeOfDay(v)}
                            placeholder="Select Day or Night"
                            style={{ width: '100%', height: '40px', backgroundColor: '#F0F5FE' }}
                        >
                            {TIME_OF_DAY_OPTIONS.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}{existingFolders.timeOfDay.includes(opt.value) ? ' (exists)' : ''}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Side
                        </h1>
                        <Select
                            value={side || undefined}
                            onChange={(v) => setSide(v)}
                            placeholder="Select LHS or RHS"
                            style={{ width: '100%', height: '40px', backgroundColor: '#F0F5FE' }}
                        >
                            {SIDE_OPTIONS.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}{existingFolders.side.includes(opt.value) ? ' (exists)' : ''}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <h1 style={{ fontSize: '1rem', fontWeight: '600', color: '#0A2540' }}>
                            Carriageway
                        </h1>
                        <Select
                            value={carriageway || undefined}
                            onChange={(v) => setCarriageway(v)}
                            placeholder="Select MCW / Service Road / Slip Road"
                            style={{ width: '100%', height: '40px', backgroundColor: '#F0F5FE' }}
                        >
                            {CARRIAGEWAY_OPTIONS.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}{existingFolders.carriageway.includes(opt.value) ? ' (exists)' : ''}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    {previewPrefix && (
                        <div style={{
                            marginBottom: 8,
                            padding: '8px 10px',
                            backgroundColor: '#F0F5FE',
                            borderRadius: 6,
                            fontSize: '0.8rem',
                            color: '#0A2540',
                            wordBreak: 'break-all',
                        }}>
                            <div><strong>Upload path:</strong> {previewPrefix}</div>
                            <a
                                href={gcsConsoleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-block', marginTop: 4, color: '#1967D2', textDecoration: 'none', fontWeight: 500 }}
                            >
                                Open in GCS Console ↗
                            </a>
                        </div>
                    )}

                    {errorMessage && (
                        <div style={{ display: 'flex', alignItems: 'center', width: 'full', height: 'fit', textAlign: 'center', backgroundColor: '#FFEEEF', color: '#DF0028', padding: 4 }}>
                            <FiAlertCircle style={{ marginRight: 4 }} /> {errorMessage}
                        </div>
                    )}

                    {isUploading && (
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                <Progress
                                    status="active"
                                    strokeColor={{ from: '#108ee9', to: '#87d068' }}
                                    percent={uploadProgress}
                                />
                            </div>
                            {/* --- NEW: per-file progress using videoFiles --- */}
                            {videoFiles.map((file, index) => (
                                <div key={index} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{ fontSize: '0.85rem', color: '#0A2540' }}>{file.name || `Video ${index + 1}`}</span>
                                        <span style={{ fontSize: '0.8rem', color: '#5F6368' }}>{(perFileProgress[index] || 0)}%</span>
                                    </div>
                                    <Progress
                                        status="active"
                                        percent={perFileProgress[index] || 0}
                                    />
                                </div>
                            ))}
                            {/* --- OLD: preparedItems.map commented out ---
                            {preparedItems.map((item, index) => (
                                <div key={item.uid} style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <span style={{ fontSize: '0.85rem', color: '#0A2540' }}>{item.video?.name || `Video ${index + 1}`}</span>
                                        <span style={{ fontSize: '0.8rem', color: '#5F6368' }}>{(perFileProgress[index] || 0)}%</span>
                                    </div>
                                    <Progress status="active" percent={perFileProgress[index] || 0} />
                                </div>
                            ))}
                            --- END OLD --- */}
                        </div>
                    )}
                    <Divider />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <Button key="back" onClick={handleCancel} style={{ borderColor: '#F82D1C', color: '#F82D1C' }}>
                            Cancel
                        </Button>
                        <Button
                            key="create-folder"
                            onClick={creatingFolder || loading ? null : handleCreateFolder}
                            disabled={creatingFolder || loading || !allFolderFieldsSelected}
                            style={{
                                borderColor: '#1967D2',
                                color: '#1967D2',
                                marginLeft: 'auto',
                                marginRight: 8,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {creatingFolder ? (
                                <Spin indicator={<LoadingOutlined spin />} size="small" style={{ marginRight: 6 }} />
                            ) : null}
                            Create folder only
                        </Button>
                        <Button key="submit" type="primary" onClick={loading ? null : handleSubmit}
                            style={{
                                backgroundColor: '#1967D2',
                                color: 'white',
                                alignItems: 'center',
                                display: 'flex',
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <Spin indicator={<LoadingOutlined spin />} size="small" style={{ color: 'white' }} />
                            ) : (
                                <MdOutlineDone size={18} style={{ marginRight: 4 }} />
                            )}
                            {/* --- NEW: simplified button text --- */}
                            {loading ? 'Uploading...' : `Upload${videoFiles.length > 0 ? ` ${videoFiles.length} file(s)` : ''}`}
                            {/* --- OLD: {loading ? (isUploading ? 'Uploading...' : 'Preparing...') : (isPrepared ? `Upload ${preparedItems.length} file(s)` : 'Prepare for upload')} --- */}
                        </Button>
                    </div>
                </div>
            </>
            <input
                type="file"
                accept="video/*"
                ref={videoInputRef}
                style={{ display: 'none' }}
                onChange={handleVideoFileChange}
                disabled={isPrepared}
                multiple
            />
            <input
                type="file"
                accept=".gpx"
                ref={gpxInputRef}
                style={{ display: 'none' }}
                onChange={handleGpxFileChange}
                disabled={isPrepared}
                multiple
            />
        </Modal>
    )
}

export default Upload
